/**
 * Liquid-first bundle UDF (simple | infinite | mixed).
 * Selection = fixed list (Liquid) + customer IO picks. Never the option catalog.
 */
(() => {
  const log = (event, detail = {}) => {
    try {
      console.info("[jafra-bundle-udf]", event, detail);
    } catch (e) {
      /* ignore */
    }
  };

  const parseJsonScript = (root, selector) => {
    const el = root?.querySelector?.(selector);
    if (!el?.textContent?.trim()) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  };

  const normalizeVariantId = (value) => {
    const id = String(value ?? "")
      .split("/")
      .pop();
    return /^\d+$/.test(id) ? id : "";
  };

  const normalizePriceCents = (raw) => {
    if (raw == null || raw === "") return null;
    const rawStr = String(raw).trim();
    const n = Number(rawStr);
    if (!Number.isFinite(n) || n <= 0) return null;

    // Dollar amounts use a decimal ("4.00", 4.5). Integers from Liquid/Shopify
    // JSON are already cents (400 = $4.00). Never multiply those by 100 —
    // that turned a $4 weight into $400 and skewed _UDF1 allocation.
    if (rawStr.includes(".")) {
      return Math.round(n * 100);
    }
    return Math.round(n);
  };

  const allocateWeightedCents = (totalCents, weights) => {
    const total = Math.round(Number(totalCents));
    const normalized = weights.map((w) => Math.max(Number(w) || 0, 0));
    const weightSum = normalized.reduce((sum, w) => sum + w, 0);
    if (total <= 0 || weightSum <= 0 || !normalized.length) return [];

    const raw = normalized.map((w) => (w / weightSum) * total);
    const allocated = raw.map((v) => Math.floor(v));
    let remainder = total - allocated.reduce((sum, v) => sum + v, 0);
    const fractions = raw
      .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
      .sort((a, b) => b.fraction - a.fraction || a.index - b.index);

    for (const entry of fractions) {
      if (remainder <= 0) break;
      allocated[entry.index] += 1;
      remainder -= 1;
    }
    return allocated;
  };

  const joinTitles = (titles) => titles.filter(Boolean).join(" <> ");
  const joinIds = (ids) => ids.filter(Boolean).join(" <> ");
  const joinPrices = (prices) => prices.filter((p) => p != null && p !== "").join(" <> ");
  const formatUdf1 = (centsList) =>
    centsList.map((cents) => (Number(cents) / 100).toFixed(2)).join("<>");

  /**
   * Simple Bundles maps parent `<>` properties 1:1 onto **component lines** in
   * definition order — never 1:1 onto expanded units. A row with
   * quantity_in_bundle > 1 still consumes a single value; that unit price applies
   * to the whole expanded cart line at any quantity.
   *
   * Algorithm (unit sale prices, never line totals):
   * 1. weight_i = unit_catalog_cents * quantity_in_bundle
   * 2. allocate parent sale across those line weights → line totals
   * 3. unit_i = floor(line_total_i / qty_i), then distribute leftover cents
   *    preferentially onto qty===1 lines so Σ(unit_i * qty_i) === saleCents
   *
   * Confirmed by Simple Bundles (Basil Khan). Do not allowlist products.
   */
  const allocateUnitUdf1ByComponent = (saleCents, components) => {
    const list = (Array.isArray(components) ? components : []).filter(
      (c) => c && (c.price > 0 || c.qty > 0),
    );
    const total = Math.round(Number(saleCents));
    if (!list.length || total <= 0) return [];

    const qtys = list.map((c) => {
      const q = Math.floor(Number(c.qty) || 1);
      return q > 0 ? q : 1;
    });
    const weights = list.map((c, i) => Math.max(Number(c.price) || 0, 0) * qtys[i]);
    if (!weights.some((w) => w > 0)) return [];

    const lineTotals = allocateWeightedCents(total, weights);
    const units = lineTotals.map((lineTotal, i) => Math.floor(lineTotal / qtys[i]));

    let reconstructed = units.reduce((sum, unit, i) => sum + unit * qtys[i], 0);
    let leftover = total - reconstructed;
    let guard = 0;
    while (leftover > 0 && guard < 10000) {
      guard += 1;
      let progressed = false;
      for (let i = 0; i < units.length && leftover > 0; i += 1) {
        if (qtys[i] === 1) {
          units[i] += 1;
          leftover -= 1;
          progressed = true;
        } else if (leftover >= qtys[i]) {
          units[i] += 1;
          leftover -= qtys[i];
          progressed = true;
        }
      }
      if (!progressed) break;
    }

    reconstructed = units.reduce((sum, unit, i) => sum + unit * qtys[i], 0);
    if (reconstructed !== total) {
      log("udf1.unit_alloc_remainder", {
        saleCents: total,
        reconstructed,
        leftover: total - reconstructed,
        qtys,
        units,
      });
    }
    return units;
  };

  const isConsultant = () =>
    window.customer?.userType === "Consultant" || window.customer?.isPartner === true;

  const orderType = () => {
    if (window.customer?.userType) return window.customer.userType;
    if (window.customer?.id) return "Retail";
    return "Guest";
  };

  const resolveForm = (root) =>
    root?.closest?.("form[action*='/cart/add']") ||
    root?.querySelector?.("form[action*='/cart/add']") ||
    document.querySelector("form[data-jafra-bundle-kind]") ||
    document.querySelector("form[action*='/cart/add']");

  const readKind = (form) =>
    String(form?.dataset?.jafraBundleKind || form?.querySelector?.('[name="properties[_jafra_bundle_kind]"]')?.value || "")
      .trim()
      .toLowerCase();

  const mergeLookup = (target, key, entry) => {
    const title = String(key || "").trim();
    if (!title || !entry?.id || !entry?.price) return;
    const existing = target[title];
    if (!existing) {
      target[title] = entry;
      return;
    }
    // Prefer higher retail weight. SB Infinite gift slots often use a stub
    // like $0.25 while catalog / Liquid has the real component retail.
    if ((entry.price || 0) > (existing.price || 0)) {
      target[title] = {
        ...existing,
        ...entry,
        id: entry.id || existing.id,
        price: entry.price,
      };
    }
  };

  const weightPriceCents = (item, fallbackCents = 0) => {
    const candidates = [
      normalizePriceCents(item?.price),
      normalizePriceCents(item?.compare_at_price ?? item?.compareAtPrice),
      normalizePriceCents(item?.retail_price ?? item?.retailPrice),
      normalizePriceCents(item?.variant_price ?? item?.variantPrice),
      fallbackCents || null,
    ].filter((n) => n != null && n > 0);
    return candidates.length ? Math.max(...candidates) : null;
  };

  const buildLookup = (form) => {
    const lookup = {};
    const fromLiquid = parseJsonScript(form, "[data-jafra-bundle-lookup]");
    if (fromLiquid && typeof fromLiquid === "object") {
      Object.entries(fromLiquid).forEach(([title, entry]) => {
        const id = normalizeVariantId(entry?.id);
        const price = normalizePriceCents(entry?.price);
        if (id && price) mergeLookup(lookup, title, { id, price });
      });
    }

    // SB app map: product_title + variant_options.value (shade labels).
    const sb2 = parseJsonScript(form.closest?.(".product-form") || document, "[data-jafra-sb-bundled-variants]") ||
      parseJsonScript(document, "[data-jafra-sb-bundled-variants]");
    if (sb2 && typeof sb2 === "object") {
      Object.values(sb2).forEach((items) => {
        const list = Array.isArray(items) ? items : [];
        list.forEach((item) => {
          if (!item || typeof item !== "object") return;
          const id = normalizeVariantId(item.variant_id ?? item.variantId ?? item.id);
          const productTitle = String(item.product_title ?? item.title ?? "").trim();
          const catalogFallback = productTitle ? lookup[productTitle]?.price || 0 : 0;
          const price = weightPriceCents(item, catalogFallback);
          if (!id || !price) return;
          const entry = { id, price };
          mergeLookup(lookup, productTitle, entry);
          const variantTitle = String(item.variant_title ?? "").trim();
          if (productTitle && variantTitle && !/^default title$/i.test(variantTitle)) {
            mergeLookup(lookup, `${productTitle} - ${variantTitle}`, entry);
          }
          (Array.isArray(item.variant_options) ? item.variant_options : []).forEach((option) => {
            mergeLookup(lookup, option?.value, entry);
          });
        });
      });
    }

    return lookup;
  };

  const readSbBundledVariants = (form) => {
    const root = form?.closest?.(".product-form") || form || document;
    return (
      parseJsonScript(root, "[data-jafra-sb-bundled-variants]") ||
      parseJsonScript(document, "[data-jafra-sb-bundled-variants]")
    );
  };

  /**
   * One Simple Bundles fixed component per definition row (with quantity_in_bundle).
   * Do NOT expand qty into duplicate rows — SB maps _UDF1 1:1 to these lines.
   * Infinite option slots are customer picks, not fixed components.
   */
  const readSb2FixedComponents = (form) => {
    const sb2 = readSbBundledVariants(form);
    if (!sb2 || typeof sb2 !== "object") return [];
    const out = [];
    Object.values(sb2).forEach((items) => {
      (Array.isArray(items) ? items : []).forEach((item) => {
        if (!item || typeof item !== "object") return;
        if (String(item.type || "") === "Infinite options") return;
        const title = String(item.product_title ?? item.title ?? "").trim();
        if (!title) return;
        const id = normalizeVariantId(item.variant_id ?? item.variantId ?? item.id);
        const price = weightPriceCents(item, 0);
        const qtyRaw = Number(item.quantity_in_bundle ?? item.quantity ?? 1);
        const qty = Number.isFinite(qtyRaw) && qtyRaw > 0 ? Math.floor(qtyRaw) : 1;
        out.push({ title, id, price, qty });
      });
    });
    return out;
  };

  const normalizeFixedComponents = (rawList) => {
    if (!Array.isArray(rawList)) return [];
    return rawList
      .map((item) => {
        const title = String(item?.title || "").trim();
        const id = normalizeVariantId(item?.id);
        const price = normalizePriceCents(item?.price);
        const qtyRaw = Number(item?.qty ?? item?.quantity_in_bundle ?? item?.quantity ?? 1);
        const qty = Number.isFinite(qtyRaw) && qtyRaw > 0 ? Math.floor(qtyRaw) : 1;
        return { title, id, price, qty };
      })
      .filter((item) => item.title);
  };

  /**
   * Prefer SB2 component lines (with qty). Fall back to Liquid markers.
   * If Liquid still emits duplicated unit rows (legacy), collapse by variant id
   * so each SKU appears once with summed qty.
   */
  const readFixedComponents = (form) => {
    const fromSb2 = readSb2FixedComponents(form);
    if (fromSb2.length) return fromSb2;

    const raw = parseJsonScript(form, "[data-jafra-bundle-fixed]");
    const fromLiquid = normalizeFixedComponents(raw);
    if (!fromLiquid.length) return [];

    // Collapse legacy per-unit duplicates (same id/title repeated) into one row.
    const collapsed = [];
    const indexByKey = new Map();
    fromLiquid.forEach((item) => {
      const key = item.id || `title:${item.title.toLowerCase()}`;
      if (indexByKey.has(key)) {
        const existing = collapsed[indexByKey.get(key)];
        existing.qty += item.qty || 1;
        if ((item.price || 0) > (existing.price || 0)) existing.price = item.price;
        return;
      }
      indexByKey.set(key, collapsed.length);
      collapsed.push({ ...item });
    });
    return collapsed;
  };

  /** @deprecated name kept via alias for clarity in call sites — fixed components, not expanded units */
  const readFixed = (form) => readFixedComponents(form);

  const readWeightPrices = (form) => {
    // Unit catalog cents in definition order (one per component). Used only when
    // component qty is unavailable; prefer readFixedComponents + allocateUnitUdf1ByComponent.
    const components = readFixedComponents(form);
    if (components.some((c) => (c.qty || 1) > 1 || c.price > 0)) {
      return components.map((c) => c.price).filter((n) => n != null && n > 0);
    }
    const raw = parseJsonScript(form, "[data-jafra-bundle-udf-weight-prices]");
    if (raw == null) return [];
    return String(raw)
      .split(/<>|,/)
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => normalizePriceCents(part))
      .filter(Boolean);
  };

  /**
   * Build consultant _UDF1 for simple packs: one UNIT sale price per component line.
   * Infinite/mixed keep equal/weighted splits over selection titles (qty usually 1).
   */
  const buildConsultantUdf1 = (kind, saleCents, form, selection) => {
    if (!isConsultant() || !(saleCents > 0)) return "";

    if (kind === "simple") {
      const components = readFixedComponents(form);
      if (components.length) {
        const units = allocateUnitUdf1ByComponent(saleCents, components);
        if (units.length) {
          log("udf1.unit_per_component", {
            components: components.map((c) => ({
              title: c.title,
              id: c.id,
              qty: c.qty,
              unitCatalog: c.price,
            })),
            units,
            saleCents,
          });
          return formatUdf1(units);
        }
      }
    }

    let weights = selection.prices.map((p) => Number(p)).filter((n) => n > 0);
    if (kind === "simple" && !weights.length) {
      weights = readWeightPrices(form);
    }

    if (weights.length) {
      // For non-simple (or simple without SB2 qty metadata): weights are already
      // one-per-selection. Do NOT multiply by qty here.
      return formatUdf1(allocateWeightedCents(saleCents, weights));
    }
    if (selection.titles.length > 1) {
      log("udf1.equal_split_fallback", { kind, titles: selection.titles });
      return formatUdf1(allocateWeightedCents(saleCents, selection.titles.map(() => 1)));
    }
    if (selection.titles.length === 1) {
      return (saleCents / 100).toFixed(2);
    }
    return "";
  };

  const isOptionSelect = (el) => {
    if (!el) return false;
    const name = String(el.name || "").toLowerCase();
    const id = String(el.id || "").toLowerCase();
    if (name === "id") return false;
    if (id === "product-select" || id.startsWith("product-select-")) return false;
    return true;
  };

  const readPicks = (form) => {
    const container =
      form?.querySelector?.("#simple-bundles-io-options") ||
      document.querySelector("#simple-bundles-io-options");
    if (!container) return [];
    return Array.from(container.querySelectorAll("select"))
      .filter(isOptionSelect)
      .map((select) => ({
        key: String(select.name || "").replace(/^properties\[(.+)\]$/, "$1") || select.id || "Choice",
        title: String(select.value || "").trim(),
      }))
      .filter((pick) => pick.title);
  };

  const resolveFromLookup = (lookup, title) => {
    const needle = String(title || "").trim();
    if (!needle || !lookup) return null;
    if (lookup[needle]) return lookup[needle];
    const lower = needle.toLowerCase();
    for (const [key, entry] of Object.entries(lookup)) {
      if (String(key).trim().toLowerCase() === lower) return entry;
    }
    let best = null;
    for (const [key, entry] of Object.entries(lookup)) {
      const keyLower = String(key).trim().toLowerCase();
      if (!keyLower || keyLower.length < 4) continue;
      if (lower.startsWith(`${keyLower} -`) || lower.startsWith(`${keyLower}–`)) {
        if (!best || keyLower.length > best.len) best = { entry, len: keyLower.length };
      }
    }
    return best?.entry || null;
  };

  /**
   * Product N first (by N), then Choice/Choose/Selection. Matches PDP select order
   * and what Simple Bundles expects for expand — not alphabetical ("CHOOSE" before "Product").
   */
  const sortPickKeys = (keys) =>
    [...keys].sort((a, b) => {
      const rank = (key) => {
        const product = String(key).trim().match(/^Product\s+(\d+)$/i);
        if (product) return [0, Number(product[1])];
        const choice = String(key)
          .trim()
          .match(/^(Choice|Choose|Selection|CHOOSE)\s+(\d+)/i);
        if (choice) return [1, Number(choice[2])];
        return [2, 0];
      };
      const [ra, na] = rank(a);
      const [rb, nb] = rank(b);
      return ra - rb || na - nb || String(a).localeCompare(String(b));
    });

  const buildSelection = (kind, fixed, picks, lookup) => {
    const titles = [];
    const ids = [];
    const prices = [];

    const pushTitle = (title) => {
      const resolved = resolveFromLookup(lookup, title);
      titles.push(title);
      if (resolved?.id) ids.push(String(resolved.id));
      if (resolved?.price) prices.push(String(resolved.price));
      else if (fixed.find?.((f) => f.title === title)?.price) {
        prices.push(String(fixed.find((f) => f.title === title).price));
        if (!resolved?.id && fixed.find((f) => f.title === title)?.id) {
          ids.push(String(fixed.find((f) => f.title === title).id));
        }
      }
    };

    if (kind === "simple") {
      fixed.forEach((item) => {
        if (!item.title) return;
        titles.push(item.title);
        if (item.id) ids.push(String(item.id));
        if (item.price) prices.push(String(item.price));
      });
    } else if (kind === "infinite") {
      picks.forEach((pick) => pushTitle(pick.title));
    } else if (kind === "mixed") {
      // Use UI select order (Product 1..N then CHOOSE), same as prod expand.
      // Do NOT rebuild from Liquid fixed list — metafield order often differs from Product N.
      if (picks.length) {
        picks.forEach((pick) => pushTitle(pick.title));
      } else {
        fixed.forEach((item) => {
          if (!item.title) return;
          titles.push(item.title);
          if (item.id) ids.push(String(item.id));
          if (item.price) prices.push(String(item.price));
        });
      }
    }

    return {
      titles,
      ids,
      prices,
      // simple: omit all selection props (SB expand from parent config)
      // mixed: titles only — prod expands with titles; stamped ids/wrong order broke expand to 2/4
      omitSelectionProps: kind === "simple",
      titlesOnly: kind === "mixed",
    };
  };

  const parentSaleCents = (form, params) => {
    const variantId = normalizeVariantId(params?.get?.("id") || form?.querySelector?.('[name="id"]')?.value);
    const meta = window.jafraAvalaraVariantMetadataCache?.[variantId];
    if (meta?.retailCents) return Math.round(Number(meta.retailCents));

    const priceEl = form?.closest?.(".product-form")?.querySelector?.("[data-product-price], [data-price]");
    const text = priceEl?.getAttribute?.("data-product-price") || priceEl?.textContent || "";
    const dollars = Number(String(text).replace(/[^0-9.]/g, ""));
    if (Number.isFinite(dollars) && dollars > 0) return Math.round(dollars * 100);

    const productJson = parseJsonScript(form.closest?.(".product-form") || document, "[data-product-json]");
    const variant = (productJson?.variants || []).find((v) => String(v.id) === variantId);
    if (variant?.price != null) return normalizePriceCents(variant.price) || Math.round(Number(variant.price));
    return 0;
  };

  const stampParams = (params, selection, udf1, kind) => {
    if (!params?.set) return;

    ["_bundle_selection", "_bundle_selection_ids", "_bundle_selection_prices", "_UDF1", "_UDF2"].forEach(
      (key) => params.delete(`properties[${key}]`),
    );

    if (!selection.omitSelectionProps && selection.titles.length) {
      params.set("properties[_bundle_selection]", joinTitles(selection.titles));
      if (!selection.titlesOnly) {
        if (selection.ids.length === selection.titles.length) {
          params.set("properties[_bundle_selection_ids]", joinIds(selection.ids));
        }
        if (selection.prices.length === selection.titles.length) {
          params.set("properties[_bundle_selection_prices]", joinPrices(selection.prices));
        }
      }
    }

    if (kind) params.set("properties[_jafra_bundle_kind]", kind);

    if (isConsultant()) {
      params.set("properties[_UDF3]", orderType());
      if (udf1) params.set("properties[_UDF1]", udf1);
    }
  };

  const stampForm = (form, selection, udf1, kind) => {
    const setProp = (key, value) => {
      let input = form.querySelector(`[name="properties[${key}]"]`);
      if (!value) {
        input?.remove();
        return;
      }
      if (!input) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = `properties[${key}]`;
        form.appendChild(input);
      }
      input.value = value;
    };

    ["_bundle_selection", "_bundle_selection_ids", "_bundle_selection_prices", "_UDF2"].forEach((key) =>
      setProp(key, ""),
    );

    if (!selection.omitSelectionProps && selection.titles.length) {
      setProp("_bundle_selection", joinTitles(selection.titles));
      if (!selection.titlesOnly) {
        if (selection.ids.length === selection.titles.length) {
          setProp("_bundle_selection_ids", joinIds(selection.ids));
        }
        if (selection.prices.length === selection.titles.length) {
          setProp("_bundle_selection_prices", joinPrices(selection.prices));
        }
      }
    }

    if (kind) setProp("_jafra_bundle_kind", kind);
    if (isConsultant()) {
      setProp("_UDF3", orderType());
      setProp("_UDF1", udf1 || "");
    } else {
      setProp("_UDF1", "");
      setProp("_UDF3", "");
    }
  };

  const prepareFromRoot = (root, params = null, options = {}) => {
    const form = resolveForm(root);
    const kind = readKind(form);
    if (!kind || !["simple", "infinite", "mixed"].includes(kind)) {
      return { handled: false };
    }

    const lookup = buildLookup(form);
    const fixed = readFixed(form);
    const picks = readPicks(form);
    const selection = buildSelection(kind, fixed, picks, lookup);

    const saleCents =
      Math.round(Number(options.parentPriceCents)) > 0
        ? Math.round(Number(options.parentPriceCents))
        : parentSaleCents(form, params);
    const udf1 = buildConsultantUdf1(kind, saleCents, form, selection);

    log("prepare", {
      kind,
      titles: selection.titles,
      ids: selection.ids,
      prices: selection.prices,
      udf1,
      omitSelectionProps: selection.omitSelectionProps,
    });

    if (params) stampParams(params, selection, udf1, kind);
    if (params?.set) {
      picks.forEach((pick) => {
        if (!pick.key || !pick.title) return;
        params.set(`properties[${pick.key}]`, pick.title);
      });
    }
    if (form) {
      stampForm(form, selection, udf1, kind);
    }

    return {
      handled: true,
      kind,
      selection,
      udf1,
      choiceCount: picks.length,
      choiceKeys: picks.map((p) => p.key),
      hasBundleSelection: !selection.omitSelectionProps && selection.titles.length > 0,
      deferredUdf1: false,
    };
  };

  /** Prepare URLSearchParams cart add. Returns null if this form is not liquid-kind stamped. */
  const prepareCartAddSearchParams = (
    params,
    sourceElement,
    existingProperties = {},
    options = {},
  ) => {
    const form = resolveForm(sourceElement);
    const kind = readKind(form);
    if (!kind) {
      const propKind = String(existingProperties?._jafra_bundle_kind || "").toLowerCase();
      if (!propKind) return null;
    }
    const result = prepareFromRoot(sourceElement || form, params, options);
    if (!result.handled) return null;
    return result;
  };

  const prepareProductForm = (form, options = {}) => {
    const result = prepareFromRoot(form, null, options);
    return result.handled ? result : null;
  };

  /**
   * Brochure / off-PDP: read the same Liquid markers from fetched product HTML.
   * @param {Document|Element} doc
   * @param {object} properties existing line properties (may include Choice*)
   * @param {number} parentPriceCents
   */
  const prepareFromDocument = (doc, properties = {}, parentPriceCents = 0) => {
    const form =
      doc.querySelector?.("form[data-jafra-bundle-kind]") ||
      doc.querySelector?.("form[action*='/cart/add']");
    if (!form) return null;
    const kind = readKind(form);
    if (!kind) return null;

    const lookup = buildLookup(form);
    const fixed = readFixed(form);
    const picksFromProps = sortPickKeys(
      Object.keys(properties || {}).filter((key) =>
        /^(Product|Choice|Choose|Selection)\b/i.test(String(key).trim()),
      ),
    )
      .map((key) => ({ key, title: String(properties[key] || "").trim() }))
      .filter((p) => p.title);
    const picks = picksFromProps.length ? picksFromProps : readPicks(form);
    const selection = buildSelection(kind, fixed, picks, lookup);

    const saleCents = Math.round(Number(parentPriceCents));
    const udf1 = buildConsultantUdf1(kind, saleCents, form, selection);

    const next = { ...properties };
    delete next._UDF2;
    if (selection.omitSelectionProps) {
      delete next._bundle_selection;
      delete next._bundle_selection_ids;
      delete next._bundle_selection_prices;
    } else if (selection.titles.length) {
      next._bundle_selection = joinTitles(selection.titles);
      if (selection.titlesOnly) {
        delete next._bundle_selection_ids;
        delete next._bundle_selection_prices;
      } else {
        if (selection.ids.length === selection.titles.length) {
          next._bundle_selection_ids = joinIds(selection.ids);
        }
        if (selection.prices.length === selection.titles.length) {
          next._bundle_selection_prices = joinPrices(selection.prices);
        }
      }
    }
    next._jafra_bundle_kind = kind;
    if (isConsultant()) {
      next._UDF3 = orderType();
      if (udf1) next._UDF1 = udf1;
    }

    return { handled: true, kind, properties: next, udf1, selection };
  };

  window.jafraBundleUdf = {
    prepareCartAddSearchParams,
    prepareProductForm,
    prepareFromDocument,
    readKind,
  };
})();
